// The sign-in page of a project's app (ADR 0021/0022): the APP's page, never cortex's. It carries the
// app's name, icon and colour (window.__CORTEX_APP__.branding) and signs in the way the runtime profile
// says: with its identity provider, straight there (autoRedirect, the default) or with one button named
// after the provider ("Sign in with Keycloak"). The app has no password of its own. Every problem (the
// provider unreachable or refusing, no access to the app, a sign-in that did not complete) and the end
// of a session are said here, and nothing goes to the provider again until the person asks.
import * as React from 'react';
import { useLocation } from 'react-router';
import { useAppAuth } from './auth.tsx';
import { appInfo } from './session.ts';

/** When the page last sent the person to the provider on its own (an unfinished sign-in must not loop). */
const ATTEMPT_KEY = 'app_sign_in_attempt';
const LOOP_WINDOW_MS = 60_000;

const attempts = {
  get(): number {
    try {
      return Number(sessionStorage.getItem(ATTEMPT_KEY) ?? 0);
    } catch {
      return 0;
    }
  },
  set(v: number | null) {
    try {
      if (v === null) sessionStorage.removeItem(ATTEMPT_KEY);
      else sessionStorage.setItem(ATTEMPT_KEY, String(v));
    } catch {
      /* storage off: the page shows the button instead of going on its own */
    }
  },
};

/** A signed-in app forgets its last automatic attempt (the next sign-out may go straight again). */
export const signInSettled = () => attempts.set(null);

const REASONS: Record<string, string> = {
  access_denied: 'The sign-in was refused. If you should have access, ask the app’s administrator.',
  discovery: 'The sign-in service is unavailable right now. Try again in a minute.',
  unknown_state: 'This sign-in expired or was not started here. Sign in again.',
  provider_error: 'The sign-in service refused the sign-in.',
  exchange: 'The sign-in service did not complete the sign-in. Try again.',
  id_token: 'The sign-in service’s answer could not be verified. Try again.',
};

export function AppSignIn() {
  const info = appInfo();
  const { state } = useAppAuth();
  const location = useLocation();
  const q = new URLSearchParams(location.search);
  const reason = q.get('sso_error');
  const signedOut = q.get('signed_out') === '1';
  const refused = state.status === 'signed-out' ? state.refused : undefined;
  const apiError = state.status === 'signed-out' ? state.error : undefined;
  const recent = Date.now() - attempts.get() < LOOP_WINDOW_MS;
  const signIn = info.signIn;
  const name = info.branding.name;

  const message = reason
    ? (REASONS[reason] ?? 'The sign-in did not complete. Sign in again.')
    : (refused ?? apiError ?? (recent && !signedOut ? 'The sign-in did not complete. Sign in again.' : null));
  const auto = Boolean(signIn?.auto) && !signedOut && !message;

  const from = (location.state as { from?: { pathname: string; search?: string } } | null)?.from;
  const go = React.useCallback(
    (automatic: boolean) => {
      if (!signIn) return;
      attempts.set(automatic ? Date.now() : null);
      const back = from ? `${from.pathname}${from.search ?? ''}` : '/';
      window.location.assign(`${signIn.start}?returnTo=${encodeURIComponent(back)}`);
    },
    [signIn, from],
  );

  React.useEffect(() => {
    if (auto) go(true);
  }, [auto, go]);

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center bg-page-bg px-4 py-12"
      data-testid="app-sign-in"
      data-mode={auto ? 'redirecting' : 'page'}
    >
      <div className="w-full max-w-[380px] bg-card-bg rounded-[10px] px-7 py-8 shadow-[0_4px_24px_0_rgba(15,23,42,0.08)] flex flex-col items-center gap-5 text-center">
        <img
          src={info.branding.icon}
          alt=""
          className="h-14 w-14 rounded-[12px] object-contain"
          data-testid="app-sign-in-icon"
        />
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[22px] font-medium leading-tight text-page-title m-0" data-testid="app-name">
            {name}
          </h1>
          <p className="text-sm text-page-muted m-0" data-testid="app-sign-in-lead">
            {auto
              ? 'Signing you in…'
              : signedOut && !message
                ? 'You are signed out.'
                : `Sign in to open ${name}.`}
          </p>
        </div>
        {message ? (
          <div
            role="alert"
            className="w-full px-4 py-3 rounded-[6px] text-sm bg-[var(--error-bg)] text-error-text text-left"
            data-testid="app-sign-in-error"
          >
            {message}
          </div>
        ) : null}
        {signIn ? (
          <button
            type="button"
            data-testid="app-sign-in-button"
            onClick={() => go(false)}
            disabled={auto}
            className="w-full h-10 rounded-[6px] text-sm font-medium text-white cursor-pointer border-0 disabled:opacity-60 disabled:cursor-default transition-opacity hover:opacity-90 bg-[var(--app-brand)]"
          >
            Sign in with {signIn.provider}
          </button>
        ) : (
          <p className="text-sm text-page-muted m-0" data-testid="app-sign-in-none">
            This app’s identity provider has no browser sign-in.
          </p>
        )}
      </div>
    </div>
  );
}
